/** Foreground wake/Live lifecycle. A provider close is not automatically permission to restart. */
export type HandsFreeMode = 'off' | 'arming' | 'waiting' | 'connecting' | 'live' | 'muted';
export interface WakeAudio {
  start(): Promise<boolean>;
  stream(): MediaStream;
  ready(): void;
  rearm(): void;
  stop(): void;
}
export interface HandsFreeOptions {
  createWake(callbacks: {onWake(): void; onError(message: string): void; onLevel?(level: number): void}): WakeAudio;
  startLive(stream: MediaStream): Promise<void>;
  stopLive(): Promise<void>;
  foreground(): boolean;
  onMode(mode: HandsFreeMode): void;
  onError(message: string): void;
  onLevel?(level: number): void;
}
export class HandsFreeVoice {
  private wake?: WakeAudio;
  private generation = 0;
  private mode: HandsFreeMode = 'off';
  constructor(private readonly options: HandsFreeOptions) {}

  async start(): Promise<void> {
    if (!['off', 'muted'].includes(this.mode) || !this.options.foreground()) return;
    const generation = ++this.generation;
    this.setMode('arming');
    const wake = this.options.createWake({
      onWake: () => {
        if (generation !== this.generation || this.mode !== 'waiting') return;
        if (!this.options.foreground()) { void this.stop(); return; }
        this.setMode('connecting');
        // The bridge keeps the first utterance in RAM until Live is ready.
        try {
          void this.options.startLive(wake.stream()).catch(() => {
            if (generation === this.generation) this.fail('Live could not connect. Tap Start to retry.');
          });
        } catch { this.fail('The microphone stopped. Tap Start to try again.'); }
      },
      onError: message => { if (generation === this.generation) this.fail(message); },
      onLevel: level => { if (generation === this.generation) this.options.onLevel?.(level); },
    });
    this.wake = wake;
    try {
      const started = await wake.start();
      if (generation !== this.generation) { wake.stop(); return; }
      if (!started || !this.options.foreground()) { await this.stop(); return; }
      this.setMode('waiting');
    } catch {
      if (generation === this.generation) this.fail('The microphone could not start. Check its permission and try again.');
    }
  }

  ready(): void {
    if (this.mode !== 'connecting') return;
    if (!this.options.foreground()) { void this.stop(); return; }
    this.wake?.ready();
    this.setMode('live');
  }

  closed(reason: string | undefined, finalReceipt: boolean): void {
    if (this.mode === 'muted' || this.mode === 'off') return;
    if (reason === 'idle_timeout' && finalReceipt && this.wake && this.options.foreground()) {
      this.wake.rearm();
      this.setMode('waiting');
      return;
    }
    // Network errors, manual closes, unconfirmed billing, and lease loss must
    // never silently create another paid session or fight another device.
    this.generation++;
    this.wake?.stop(); this.wake = undefined;
    this.setMode('off');
  }

  async mute(): Promise<void> {
    this.generation++;
    this.wake?.stop(); this.wake = undefined;
    this.setMode('muted');
    await this.options.stopLive();
  }

  async stop(): Promise<void> {
    this.generation++;
    this.wake?.stop(); this.wake = undefined;
    this.setMode('off');
    await this.options.stopLive();
  }

  private fail(message: string): void {
    this.generation++;
    this.wake?.stop(); this.wake = undefined;
    this.setMode('off');
    void this.options.stopLive();
    this.options.onError(message);
  }
  private setMode(mode: HandsFreeMode): void { this.mode = mode; this.options.onMode(mode); }
}
